/**
 * Minimal react-native shim for web.
 * Replaces react-native-web so className passes through cleanly and our
 * Tailwind classes actually apply.
 */
import {
  forwardRef,
  useImperativeHandle,
  useRef,
  type HTMLAttributes,
  type ButtonHTMLAttributes,
  type InputHTMLAttributes,
  type MouseEvent,
  type CSSProperties,
  type ChangeEvent,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
  type Ref,
} from 'react';

// ── Style helpers ──────────────────────────────────────────────────────────

type Transform = { translateX: number | string } | { translateY: number | string } | { scale: number } | { scaleX: number } | { scaleY: number } | { rotate: string };
export type ViewStyle = Omit<CSSProperties, 'transform'> & {
  transform?: string | Transform[];
  marginHorizontal?: number | string;
  marginVertical?: number | string;
  paddingHorizontal?: number | string;
  paddingVertical?: number | string;
};
type RNStyle = ViewStyle;
type Style = RNStyle | false | null | undefined | Style[];

export function rnStyleToCss(
  style: Style,
): CSSProperties | undefined {
  if (!style) return undefined;
  const arr: RNStyle[] = [];
  const collect = (value: Style): void => {
    if (Array.isArray(value)) value.forEach(collect);
    else if (value) arr.push(value);
  };
  collect(style);
  const out: CSSProperties = {};
  for (const s of arr) {
    if (!s || typeof s !== 'object') continue;
    const { marginHorizontal, marginVertical, paddingHorizontal, paddingVertical, transform, ...rest } =
      s as RNStyle;
    if (marginHorizontal !== undefined) {
      out.marginLeft = marginHorizontal;
      out.marginRight = marginHorizontal;
    }
    if (marginVertical !== undefined) {
      out.marginTop = marginVertical;
      out.marginBottom = marginVertical;
    }
    if (paddingHorizontal !== undefined) {
      out.paddingLeft = paddingHorizontal;
      out.paddingRight = paddingHorizontal;
    }
    if (paddingVertical !== undefined) {
      out.paddingTop = paddingVertical;
      out.paddingBottom = paddingVertical;
    }
    if (transform !== undefined) {
      out.transform = typeof transform === 'string' ? transform : transform.map((entry) => {
        const [name, value] = Object.entries(entry)[0];
        const unit = name.startsWith('translate') && typeof value === 'number' ? 'px' : '';
        return `${name}(${value}${unit})`;
      }).join(' ');
    }
    Object.assign(out, rest);
  }
  return out;
}

// ── Primitives ─────────────────────────────────────────────────────────────

type ViewProps = Omit<HTMLAttributes<HTMLDivElement>, 'style'> & {
  children?: ReactNode;
  className?: string;
  style?: Style;
  onLayout?: (e: { nativeEvent: { layout: { x: number; y: number; width: number; height: number } } }) => void;
  pointerEvents?: 'auto' | 'none' | 'box-none' | 'box-only';
};

export const View = forwardRef<HTMLDivElement, ViewProps>(function View(
  { children, className, style, onLayout, pointerEvents, ...rest },
  ref,
) {
  const handleRef = (el: HTMLDivElement | null) => {
    if (ref) {
      if (typeof ref === 'function') ref(el);
      else (ref as { current: HTMLDivElement | null }).current = el;
    }
    if (onLayout && el) {
      const rect = el.getBoundingClientRect();
      onLayout({
        nativeEvent: {
          layout: {
            x: el.offsetLeft,
            y: el.offsetTop,
            width: rect.width,
            height: rect.height,
          },
        },
      });
    }
  };

  return (
    <div
      ref={handleRef}
      className={className}
      data-rn-view
      style={{
        ...(pointerEvents ? { pointerEvents: pointerEvents === 'none' || pointerEvents === 'box-none' ? 'none' : 'auto' } : {}),
        ...rnStyleToCss(style),
      }}
      {...rest}
    >
      {children}
    </div>
  );
});

type TextProps = Omit<HTMLAttributes<HTMLSpanElement>, 'style'> & {
  children?: ReactNode;
  className?: string;
  style?: Style;
  onPress?: () => void;
  numberOfLines?: number;
};

export const Text = forwardRef<HTMLSpanElement, TextProps>(function Text(
  { children, className, style, onPress, numberOfLines, ...rest },
  ref,
) {
  return (
    <span
      ref={ref}
      className={className}
      onClick={onPress}
      style={{
        ...(onPress ? { cursor: 'pointer' } : {}),
        ...(numberOfLines
          ? {
              display: '-webkit-box',
              WebkitLineClamp: numberOfLines,
              WebkitBoxOrient: 'vertical' as const,
              overflow: 'hidden',
            }
          : {}),
        ...rnStyleToCss(style),
      }}
      {...rest}
    >
      {children}
    </span>
  );
});

type PressableProps = Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'style'> & {
  accessibilityLabel?: string;
  children?: ReactNode;
  className?: string;
  style?: Style;
  onPress?: (e: MouseEvent<HTMLButtonElement>) => void;
  disabled?: boolean;
  hitSlop?: number | { top?: number; right?: number; bottom?: number; left?: number };
};

export const Pressable = forwardRef<HTMLButtonElement, PressableProps>(function Pressable(
  { children, className, style, onPress, disabled, hitSlop, accessibilityLabel, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type="button"
      className={className}
      disabled={disabled}
      onClick={(e) => {
        if (disabled) return;
        onPress?.(e);
      }}
      aria-label={accessibilityLabel}
      data-rn-pressable
      style={{
        cursor: disabled ? 'default' : 'pointer',
        ...rnStyleToCss(style),
      }}
      {...rest}
    >
      {children}
    </button>
  );
});

type ScrollViewProps = Omit<HTMLAttributes<HTMLDivElement>, 'style'> & {
  children?: ReactNode;
  className?: string;
  style?: Style;
  contentContainerStyle?: Style;
  horizontal?: boolean;
  showsVerticalScrollIndicator?: boolean;
  showsHorizontalScrollIndicator?: boolean;
};

export type ScrollView = {
  scrollTo: (opts: { y?: number; x?: number; animated?: boolean }) => void;
  scrollToEnd: (opts?: { animated?: boolean }) => void;
};

export const ScrollView = forwardRef<ScrollView, ScrollViewProps>(
  function ScrollView(
    { children, className, style, contentContainerStyle, horizontal,
      showsVerticalScrollIndicator, showsHorizontalScrollIndicator, ...rest },
    ref,
  ) {
    const elementRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
      scrollTo: (opts) => elementRef.current?.scrollTo({
        top: opts.y,
        left: opts.x,
        behavior: opts.animated ? 'smooth' : 'auto',
      }),
      scrollToEnd: (opts) => {
        const el = elementRef.current;
        if (!el) return;
        el.scrollTo({
          top: horizontal ? undefined : el.scrollHeight,
          left: horizontal ? el.scrollWidth : undefined,
          behavior: opts?.animated ? 'smooth' : 'auto',
        });
      },
    }), [horizontal]);

    return (
      <div
        ref={elementRef}
        className={className}
        data-rn-scrollview
        style={{
          flexDirection: horizontal ? 'row' : 'column',
          overflow: horizontal ? 'auto hidden' : 'hidden auto',
          scrollbarWidth: (horizontal ? showsHorizontalScrollIndicator : showsVerticalScrollIndicator) === false ? 'none' : undefined,
          ...rnStyleToCss(style),
        }}
        {...rest}
      >
        <div
          data-rn-scrollview-content
          style={{
            flexDirection: horizontal ? 'row' : 'column',
            // Never let the content box shrink to fit the scroll viewport —
            // it must keep its intrinsic size so the parent actually scrolls
            // (otherwise flex compresses the children and nothing scrolls).
            flexShrink: 0,
            ...rnStyleToCss(contentContainerStyle),
          }}
        >
          {children}
        </div>
      </div>
    );
  },
);

export type TextInput = HTMLInputElement;

type TextInputProps = Omit<InputHTMLAttributes<HTMLInputElement>, 'style' | 'onChange'> & {
  value?: string;
  defaultValue?: string;
  onChangeText?: (text: string) => void;
  onSubmitEditing?: () => void;
  placeholder?: string;
  placeholderTextColor?: string;
  className?: string;
  style?: Style;
  keyboardType?: string;
  inputMode?: InputHTMLAttributes<HTMLInputElement>['inputMode'];
  autoCapitalize?: string;
  autoComplete?: string;
  returnKeyType?: string;
  maxLength?: number;
  selectTextOnFocus?: boolean;
  secureTextEntry?: boolean;
  multiline?: boolean;
  editable?: boolean;
  accessibilityLabel?: string;
};

export const TextInput = forwardRef<HTMLInputElement, TextInputProps>(function TextInput(
  {
    value,
    defaultValue,
    onChangeText,
    onSubmitEditing,
    placeholder,
    placeholderTextColor,
    className,
    style,
    keyboardType,
    inputMode,
    autoCapitalize,
    autoComplete,
    returnKeyType,
    maxLength,
    selectTextOnFocus,
    secureTextEntry,
    multiline,
    editable,
    accessibilityLabel,
    ...rest
  },
  ref,
) {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    onChangeText?.(e.target.value);
  };
  const handleKeyDown = (e: ReactKeyboardEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !multiline) {
      onSubmitEditing?.();
    }
  };
  const handleFocus = (e: any) => {
    if (selectTextOnFocus) e.target?.select?.();
  };

  const commonProps = {
    value,
    defaultValue,
    placeholder,
    onChange: handleChange,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    className,
    autoCapitalize,
    autoComplete,
    inputMode: inputMode ?? (keyboardType === 'decimal-pad' ? 'decimal' : keyboardType === 'number-pad' || keyboardType === 'numeric' ? 'numeric' : keyboardType === 'email-address' ? 'email' : undefined),
    readOnly: editable === false,
    'aria-label': accessibilityLabel,
    maxLength,
    style: {
      ...(placeholderTextColor ? ({ '--ph': placeholderTextColor } as any) : {}),
      ...rnStyleToCss(style),
    },
    ...rest,
  };

  if (multiline) {
    return <textarea ref={ref as any} {...(commonProps as any)} />;
  }

  return (
    <input
      ref={ref}
      type={secureTextEntry ? 'password' : 'text'}
      {...(commonProps as any)}
    />
  );
});

// ── Modal (portal) ─────────────────────────────────────────────────────────

type ModalProps = {
  visible: boolean;
  transparent?: boolean;
  animationType?: 'none' | 'slide' | 'fade';
  onRequestClose?: () => void;
  children?: ReactNode;
};

export function Modal({ visible, transparent, onRequestClose, children }: ModalProps) {
  if (!visible) return null;
  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 9999,
        display: 'flex',
        background: transparent ? 'transparent' : '#FAFAFA',
      }}
      onKeyDown={(e) => {
        if (e.key === 'Escape') onRequestClose?.();
      }}
    >
      {children}
    </div>
  );
}

// ── KeyboardAvoidingView — passthrough on web ──────────────────────────────

export function KeyboardAvoidingView({
  children,
  className,
  style,
}: {
  children?: ReactNode;
  className?: string;
  style?: Style;
  behavior?: string;
}) {
  return (
    <div
      className={className}
      data-rn-view
      style={{ flex: 1, ...rnStyleToCss(style) }}
    >
      {children}
    </div>
  );
}

// ── ActivityIndicator ──────────────────────────────────────────────────────

export function ActivityIndicator({ color = '#FF5A1F', size = 'small' }: { color?: string; size?: 'small' | 'large' | number }) {
  const d = typeof size === 'number' ? size : size === 'large' ? 36 : 20;
  return (
    <svg
      width={d}
      height={d}
      viewBox="0 0 50 50"
      style={{ animation: 'halvn-spin 0.9s linear infinite' }}
    >
      <style>{`@keyframes halvn-spin{from{transform:rotate(0)}to{transform:rotate(360deg)}}`}</style>
      <circle cx="25" cy="25" r="20" stroke={color} strokeWidth="4" strokeLinecap="round" fill="none" strokeDasharray="80 40" />
    </svg>
  );
}

// ── Platform / Dimensions / StyleSheet / Keyboard ──────────────────────────

export const Platform = {
  OS: 'web' as 'web' | 'ios' | 'android',
  select<T>(spec: { web?: T; default?: T; ios?: T; android?: T; native?: T }): T | undefined {
    if ('web' in spec) return spec.web;
    return spec.default;
  },
};

export const Dimensions = {
  get(_: 'window' | 'screen') {
    if (typeof window === 'undefined') return { width: 1024, height: 768, scale: 1, fontScale: 1 };
    return { width: window.innerWidth, height: window.innerHeight, scale: window.devicePixelRatio, fontScale: 1 };
  },
  addEventListener() {
    return { remove: () => undefined };
  },
};

export const StyleSheet = {
  create<T extends Record<string, RNStyle>>(styles: T): T {
    return styles;
  },
  flatten(s: RNStyle | RNStyle[] | undefined): RNStyle {
    if (!s) return {};
    return Array.isArray(s) ? Object.assign({}, ...s) : s;
  },
  absoluteFill: { position: 'absolute', top: 0, left: 0, right: 0, bottom: 0 } as RNStyle,
};

export const Keyboard = {
  dismiss() {
    if (typeof document !== 'undefined' && document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  },
};

// ── Re-export safe-area shim members so RN-style imports work ──────────────
export type { Ref };

// Default export for `import RN from 'react-native'` usage (rare)
export default {
  View,
  Text,
  Pressable,
  ScrollView,
  TextInput,
  Modal,
  KeyboardAvoidingView,
  ActivityIndicator,
  Platform,
  Dimensions,
  StyleSheet,
  Keyboard,
};
